import axios from '../axios';
import { buildGetRegionalBranchZipcodeAnalyticsUrl } from '../ApiUrl';
import {
  getRegionalBranchZipcodeAnalyticsCache,
  regionalBranchZipcodeAnalyticsCacheKey,
  setRegionalBranchZipcodeAnalyticsCache,
} from '../../utils/regionalBranchZipcodeAnalyticsCache';

function parseAnalyticsApiResponse(res) {
  if (res.hasError) {
    const message =
      typeof res.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'Could not load zipcode analytics.';
    return { ok: false, error: message, data: null };
  }
  const body = res.data;
  if (body?.success === false) {
    return {
      ok: false,
      error:
        typeof body.message === 'string' && body.message.trim()
          ? body.message.trim()
          : 'Request failed.',
      data: null,
    };
  }
  if (body?.success === true && body.data) {
    return { ok: true, error: null, data: body.data };
  }
  return { ok: false, error: 'Unexpected response from analytics API.', data: null };
}

/**
 * GET /GetRegionalBranchZipcodeAnalytics?managing_branch_name=...
 * @param {string} managingBranchName — regional branch slug (trimmed + lowercased).
 */
export const getRegionalBranchZipcodeAnalyticsAPI = async (managingBranchName) => {
  const name = regionalBranchZipcodeAnalyticsCacheKey(managingBranchName);
  if (!name) {
    return { hasError: true, message: 'managing_branch_name is required.' };
  }
  try {
    const response = await axios.get(buildGetRegionalBranchZipcodeAnalyticsUrl(name));
    return response;
  } catch (error) {
    console.log('getRegionalBranchZipcodeAnalyticsAPI catch', error);
    return { hasError: true, error };
  }
};

/**
 * Returns cached analytics when available; otherwise fetches and stores in memory.
 * @param {string} managingBranchName
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<{ ok: boolean, data: object|null, error: string|null, fromCache: boolean, refreshedAt: number|null }>}
 */
export async function loadRegionalBranchZipcodeAnalytics(managingBranchName, { forceRefresh = false } = {}) {
  const branch = regionalBranchZipcodeAnalyticsCacheKey(managingBranchName);
  if (!branch) {
    return {
      ok: false,
      data: null,
      error: 'managing_branch_name is required.',
      fromCache: false,
      refreshedAt: null,
    };
  }

  if (!forceRefresh) {
    const cached = getRegionalBranchZipcodeAnalyticsCache(branch);
    if (cached?.data) {
      return {
        ok: true,
        data: cached.data,
        error: null,
        fromCache: true,
        refreshedAt: cached.refreshedAt ?? null,
      };
    }
  }

  const res = await getRegionalBranchZipcodeAnalyticsAPI(branch);
  const parsed = parseAnalyticsApiResponse(res);
  if (!parsed.ok) {
    return {
      ok: false,
      data: null,
      error: parsed.error,
      fromCache: false,
      refreshedAt: null,
    };
  }

  const refreshedAt = Date.now();
  setRegionalBranchZipcodeAnalyticsCache(branch, { data: parsed.data, refreshedAt });

  return {
    ok: true,
    data: parsed.data,
    error: null,
    fromCache: false,
    refreshedAt,
  };
};
